import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    message: { type: String, default: 'You have unsaved changes!\nAre you sure you want to continue?' }
  }

  connect() {
    this.form = this.element
    this.changeDirectUpload = false
    this.isDirty = false
    this.initialValues = {}
    this.submitListenerAdded = false
    this.modalResolve = null
    this.isInitializing = true
    this.modalTarget = document.querySelector("#dirty-form-modal")
    if(this.modalTarget) this.setUpModalBtn()

    this.fields = [...this.form.elements]

    delete window.allowRedirect
    this.setFormHandlers()
    if(this.modalTarget) {
      this.setupModalHandlers()
    }
    this.setupMutationObserver()
    setTimeout(() => {
      this.setupFields()
      this.isInitializing = false
    }, 2000)
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      // Ignore mutations during initialization to prevent false dirty detection
      if (this.isInitializing) {
        return
      }

      let shouldRefreshFields = false

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const hasFormFields = node.querySelectorAll &&
                (node.querySelectorAll('input, select, textarea').length > 0 ||
                 node.matches && node.matches('input, select, textarea'))
              if (hasFormFields) {
                shouldRefreshFields = true
              }
            }
          })

          // Check if any removed nodes contained form fields
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const hasFormFields = node.querySelectorAll &&
                (node.querySelectorAll('input, select, textarea').length > 0 ||
                 node.matches && node.matches('input, select, textarea'))
              if (hasFormFields) {
                shouldRefreshFields = true
              }
            }
          })
        } else if (mutation.type === 'attributes') {
          if (mutation.target.matches && mutation.target.matches('input, select, textarea')) {
            if (mutation.target.name && mutation.target.name.includes('_destroy')) {
              shouldRefreshFields = true
            }
          }
        }
      })

      if (shouldRefreshFields) {
        setTimeout(() => {
          this.refreshFields()
        }, 10)
      }
    })

    this.observer.observe(this.form, {
      attributes: true,
      childList: true,
      subtree: true
    })
  }

  refreshFields() {
    const currentFieldNames = new Set()
    const newFields = [...this.form.elements]

    newFields.forEach(field => {
      if (field.name && field.type !== 'submit' && field.type !== 'button' && field.type !== 'hidden') {
        currentFieldNames.add(field.name)
      }
    })

    // Clean up initial values for fields that no longer exist
    Object.keys(this.initialValues).forEach(fieldName => {
      if (!currentFieldNames.has(fieldName)) {
        delete this.initialValues[fieldName]
      }
    })

    // Update fields list
    this.fields = newFields

    // Set up new fields
    this.setupFields()

    // Update link handlers for new/removed links
    this.refreshLinkHandlers()

    // Check if form is still dirty after field changes
    this.checkAllFieldsDirty()
  }

  refreshLinkHandlers() {
    // Remove old link handlers
    this.linkClickHandlers.forEach(({ link, handler }) => {
      link.removeEventListener('click', handler)
    })
    this.linkClickHandlers = []

    // Add new link handlers (including remove association handlers)
    this.setupLinkHandlers()
  }

  setUpModalBtn() {
    this.cancelBtn = this.modalTarget.querySelector('#cancelBtn')
    this.confirmBtn = this.modalTarget.querySelector('#confirmBtn')

    this.cancelBtn.addEventListener('click', this.cancel.bind(this))
    this.confirmBtn.addEventListener('click', this.confirm.bind(this))
  }

  setupModalHandlers() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isModalVisible()) {
        this.cancel()
      }
    }
    document.addEventListener('keydown', this.escapeHandler)

    this.modalTarget.addEventListener('click', (e) => {
      if (e.target === this.modalTarget) {
        this.cancel()
      }
    })
  }

  isModalVisible() {
    return !this.modalTarget.classList.contains('hidden')
  }

  get dirtyLinks() {
    const links = []
    const documentLinks = document.querySelectorAll('a:not([target="_blank"])')
    const formLinksSet = new Set(this.form.querySelectorAll('a'))

    for (const link of documentLinks) {
      if (!formLinksSet.has(link)) {
        links.push(link)
      }
    }
    return links
  }

  fireChangeEvent(event) {
    const customEvent = new CustomEvent('dirty-form:change', {
      detail: { originalEvent: event, isDirty: this.isDirty }
    })
    this.element.dispatchEvent(customEvent)
  }

  setupFields() {
    this.fields.forEach(field => {
      if (!field.name || field.type == 'submit' || field.type == 'button') {
        return;
      }

      // Include hidden fields (like _destroy fields) for dirty form detection
      // Skip other hidden fields that don't affect form state
      if (field.type == 'hidden' && !field.name.includes('_destroy')) {
        return;
      }

      // Only set initial value if we don't already have one for this field
      // This prevents overwriting values when fields are refreshed
      if (!this.initialValues.hasOwnProperty(field.name)) {
        this.initialValues[field.name] = this.getFieldValue(field)
      }

      // Remove existing listeners to prevent duplicates
      field.removeEventListener('change', this.handleFieldChange.bind(this))
      field.removeEventListener('input', this.handleFieldChange.bind(this))

      // Add fresh listeners
      field.addEventListener('change', this.handleFieldChange.bind(this))
      field.addEventListener('input', this.handleFieldChange.bind(this))
    })
  }

  getFieldValue(field) {
    if (field.type === 'checkbox' || field.type === 'radio') {
      return field.checked
    }
    return field.value
  }

  setFormHandlers() {
    // Handle leaving page
    window.onbeforeunload = () => {
      if (this.isDirty) {
        return this.messageValue;
      }
    };

    // Handle Turbo navigation
    this.turboBeforeVisitHandler = async (event) => {
      if (this.isDirty && !window.allowRedirect) {
        event.preventDefault()
        const confirmed = await this.showModal()
        if (confirmed) {
          this.isDirty = false
          window.allowRedirect = true
          // Re-trigger the navigation
          if (event.detail && event.detail.url) {
            window.location.href = event.detail.url
          }
        }
      }
    }
    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)

    this.setupLinkHandlers()
  }

  setupLinkHandlers() {
    this.linkClickHandlers = []

    // Handle links outside the form
    this.dirtyLinks.forEach(link => {
      const handler = async (event) => {
        if(!this.isDirty || (this.changeDirectUpload && window.allowRedirect)) {
          return;
        }

        if (this.isDirty) {
          event.preventDefault()
          const confirmed = await this.showModal()
          if (confirmed) {
            this.isDirty = false
            window.allowRedirect = true
            // Re-trigger the link click
            link.click()
          }
        }
      }

      link.addEventListener('click', handler)
      this.linkClickHandlers.push({ link, handler })
    })

    // Handle remove association links inside the form
    this.setupRemoveAssociationHandlers()
  }

  setupRemoveAssociationHandlers() {
    // Find all remove association links within the form
    // Cocoon generates different attributes for existing vs new items:
    // - Existing items: link_to_remove_association generates links with class "remove_fields"
    // - New items: dynamically added items have data-association-insertion-method="remove"
    const removeLinks = this.form.querySelectorAll('a.remove_fields, a[data-association-insertion-method="remove"]')

    removeLinks.forEach((link) => {
      const handler = async (event) => {
        if(!this.isDirty || (this.changeDirectUpload && window.allowRedirect)) {
          return;
        }

        if (this.isDirty) {
          event.preventDefault()
          const confirmed = await this.showModal()
          if (confirmed) {
            this.isDirty = false
            window.allowRedirect = true
            // Re-trigger the link click
            link.click()
          }
        }
      }

      link.addEventListener('click', handler)
      this.linkClickHandlers.push({ link, handler })
    })
  }

  showModal() {
    return new Promise((resolve) => {
      this.modalResolve = resolve
      const messageTarget = this.modalTarget.querySelector('#dirty-modal-message')

      // Update message content
      messageTarget.innerHTML = this.messageValue.replace(/\n/g, '<br>')

      this.modalTarget.classList.remove('hidden')
    })
  }

  hideModal() {
    this.modalTarget.classList.add('hidden')
  }

  cancel() {
    this.hideModal()
    if (this.modalResolve) {
      this.modalResolve(false)
      this.modalResolve = null
    }
  }

  confirm() {
    this.hideModal()
    if (this.modalResolve) {
      this.modalResolve(true)
      this.modalResolve = null
    }
  }

  handleFieldChange(event) {
    // Ignore field changes during initialization to prevent false dirty detection
    if (this.isInitializing) {
      return
    }

    const field = event.target
    const { directUploadUrl } = field.dataset

    if (directUploadUrl) {
      this.changeDirectUpload = true
    }

    if (!this.submitListenerAdded) {
      this.submitHandler = this.changeDirectUpload ?
        this.enableDirtyForm.bind(this) :
        this.handleSubmit.bind(this)

      this.form.addEventListener('submit', this.submitHandler)
      this.submitListenerAdded = true
    }

    const currentValue = this.getFieldValue(field)
    const initialValue = this.initialValues[field.name]

    if (initialValue !== currentValue) {
      this.setDirty(true)
    } else {
      this.checkAllFieldsDirty()
    }

    this.fireChangeEvent(event)
  }

  checkAllFieldsDirty() {
    const anyFieldDirty = this.fields.some(field => {
      if (!field.name ||
          field.type === 'submit' ||
          field.type === 'button' ||
          !this.form.contains(field)) { // Ensure field still exists in form
        return false
      }

      // Include hidden _destroy fields for dirty form detection
      if (field.type === 'hidden' && !field.name.includes('_destroy')) {
        return false
      }

      // Only check fields that have initial values recorded
      if (!this.initialValues.hasOwnProperty(field.name)) {
        return false
      }

      const isDirty = this.initialValues[field.name] !== this.getFieldValue(field)
      return isDirty
    })

    this.setDirty(anyFieldDirty)
  }

  setDirty(dirty) {
    const wasClean = !this.isDirty
    this.isDirty = dirty

    if (wasClean && dirty) {
      this.element.dispatchEvent(new CustomEvent('dirty-form:dirty'))
    } else if (!wasClean && !dirty) {
      this.element.dispatchEvent(new CustomEvent('dirty-form:clean'))
    }
  }

  enableDirtyForm() {
    this.setDirty(true)
  }

  handleSubmit() {
    this.setDirty(false)
  }

  disconnect() {
    // Disconnect mutation observer
    if (this.observer) {
      this.observer.disconnect()
    }

    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    document.removeEventListener('keydown', this.escapeHandler)

    if (this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler)
    }

    this.linkClickHandlers.forEach(({ link, handler }) => {
      link.removeEventListener('click', handler)
    })

    this.setDirty(false)
  }
}
